/**
 * DestinationBadge — toont een landcode + canonieke landnaam voor zendingen
 * en consolidaties. Gebruikt voor visuele consistentie van bestemming-info.
 *
 * Wanneer code=null (onbekend land) toont de badge een waarschuwende rode
 * versie zodat de medewerker direct ziet dat de bestemming niet eenduidig
 * herkend is.
 */
import React from "react";
import { MapPin, AlertCircle } from "lucide-react";

const FLAG_BY_CODE = {
  CW:    "🇨🇼",
  AW:    "🇦🇼",
  "BQ-BO": "🏝️",
  SX:    "🇸🇽",
  NL:    "🇳🇱",
  SR:    "🇸🇷",
  JM:    "🇯🇲",
  DO:    "🇩🇴",
  CO:    "🇨🇴",
  GY:    "🇬🇾",
  PA:    "🇵🇦",
  VE:    "🇻🇪",
  BE:    "🇧🇪",
  DE:    "🇩🇪",
  FR:    "🇫🇷",
  ES:    "🇪🇸",
  PT:    "🇵🇹",
  GB:    "🇬🇧",
  US:    "🇺🇸",
  CN:    "🇨🇳",
  TT:    "🇹🇹",
  BB:    "🇧🇧",
  HT:    "🇭🇹",
  BR:    "🇧🇷",
};

export default function DestinationBadge({ code, name, size = "sm", showFlag = true, testId }) {
  const hasCode = !!(code && String(code).trim());
  const display = hasCode ? (name || code) : (name || "Onbekend");
  const sizeCls = size === "lg"
    ? "px-2.5 py-1 text-xs"
    : "px-2 py-0.5 text-[10px]";
  if (!hasCode) {
    return (
      <span
        className={`inline-flex items-center gap-1 font-bold uppercase tracking-wide border bg-red-50 text-red-800 border-red-300 ${sizeCls}`}
        data-testid={testId || "destination-badge-unknown"}
        title="Bestemming niet eenduidig herkend"
      >
        <AlertCircle className="w-3 h-3" />
        {display}
      </span>
    );
  }
  const flag = FLAG_BY_CODE[code] || "";
  return (
    <span
      className={`inline-flex items-center gap-1 font-bold uppercase tracking-wide border bg-blue-50 text-blue-900 border-blue-200 ${sizeCls}`}
      data-testid={testId || `destination-badge-${code}`}
      title={`${display} (${code})`}
    >
      {showFlag && flag ? <span aria-hidden="true">{flag}</span> : <MapPin className="w-3 h-3" />}
      {display}
    </span>
  );
}
